<template>
    <span v-clickoutside="close">
        <slot name="trigger" v-if="!triggerTarget" />
        <transition
            enter-active-class="transition-opacity ease-out duration-200"
            enter-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity ease-in duration-100"
            leave-class="opacity-100"
            leave-to-class="opacity-0"
            @after-leave="onAfterLeave"
            @enter="onEnter"
        >
            <span
                :class="className"
                ref="popover"
                v-on="$listeners"
                v-show="show"
            >
                <slot />
            </span>
        </transition>
    </span>
</template>

<script>
    import { createPopper } from '@popperjs/core';

    export default {
        props: {
            triggerTarget: {
                type: null,
                default: null,
            },
            trigger: {
                type: String,
                default: 'click',
                validator: (value) => ['click', 'hover', 'manual'].includes(value),
            },
            sameWidth: {
                type: Boolean,
                default: false,
            },
            strategy: {
                type: String,
                default: 'fixed',
                validator: (value) => ['absolute', 'fixed'].includes(value),
            },
            placement: {
                type: String,
                default: 'top',
                validator: (value) => [
                    'auto', 'auto-start', 'auto-end',
                    'top', 'top-start', 'top-end',
                    'bottom', 'bottom-start', 'bottom-end',
                    'right', 'right-start', 'right-end',
                    'left', 'left-start', 'left-end',
                ].includes(value),
            },
            className: {
                type: null,
                default: '',
            },
            closeDelay: {
                type: Number,
                default: 100,
            },
            offset: {
                type: Number,
                default: 4,
            },
        },

        data: () => ({
            show: false,
            hoveringTrigger: false,
            hoveringPopover: false,
            popper: null,
            triggerEl: null,
        }),

        methods: {
            initPopper() {
                if (!this.$el || this.popper) {
                    return;
                }

                const modifiers = [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, this.offset],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            boundary: document.body,
                        },
                    },
                ];

                if (this.sameWidth) {
                    modifiers.push({
                        name: 'sameWidth',
                        enabled: true,
                        phase: 'beforeWrite',
                        requires: ['computeStyles'],
                        fn: ({ state }) => {
                            state.styles.popper.width = `${state.rects.reference.width}px`;
                        },
                        effect: ({ state }) => {
                            state.elements.popper.style.width = `${
                                state.elements.reference.offsetWidth
                            }px`;
                        },
                    });
                }

                this.popper = createPopper(this.triggerEl, this.$refs.popover, {
                    placement: this.placement,
                    strategy: this.strategy,
                    modifiers,
                });
            },

            destroyPopper() {
                if (this.popper) {
                    this.popper.destroy();
                    this.popper = null;
                }
            },

            toggle() {
                this.show ? this.close() : this.open();
            },

            open() {
                this.show = true;
            },

            close() {
                this.show = false;
            },

            shouldClose() {
                setTimeout(() => {
                    if (!this.hoveringTrigger && !this.hoveringPopover) {
                        this.close();
                    }
                }, this.closeDelay);
            },

            onEnter() {
                this.initPopper();
                this.$emit('enter');
            },

            onAfterLeave() {
                this.destroyPopper();
            },

            onTriggerClick() {
                this.toggle();
            },

            onTriggerMouseEnter() {
                this.hoveringTrigger = true;
                this.open();
            },

            onTriggerMouseLeave() {
                this.hoveringTrigger = false;
                this.shouldClose();
            },

            onPopoverMouseEnter() {
                this.hoveringPopover = true;
            },

            onPopoverMouseLeave() {
                this.hoveringPopover = false;
                this.shouldClose();
            },

            addListeners() {
                if (this.trigger === 'click') {
                    this.triggerEl.addEventListener('click', this.onTriggerClick);
                }

                if (this.trigger === 'hover') {
                    this.triggerEl.addEventListener('mouseenter', this.onTriggerMouseEnter);
                    this.triggerEl.addEventListener('mouseleave', this.onTriggerMouseLeave);
                    this.$refs.popover.addEventListener('mouseenter', this.onPopoverMouseEnter);
                    this.$refs.popover.addEventListener('mouseleave', this.onPopoverMouseLeave);
                }
            },

            removeListeners() {
                this.triggerEl.removeEventListener('click', this.onTriggerClick);
                this.triggerEl.removeEventListener('mouseenter', this.onTriggerMouseEnter);
                this.triggerEl.removeEventListener('mouseleave', this.onTriggerMouseLeave);
                this.$refs.popover.removeEventListener('mouseenter', this.onPopoverMouseEnter);
                this.$refs.popover.removeEventListener('mouseleave', this.onPopoverMouseLeave);
            },
        },

        mounted() {
            this.triggerEl = this.triggerTarget || this.$el.children[0];
            this.addListeners();
        },

        beforeDestroy() {
            this.removeListeners();
            this.destroyPopper();
        },
    };
</script>
